/**
 * Tenant-specific configuration. Tenant ID and SharePoint site location are
 * committed (they are identifiers, not secrets). The Entra client ID varies
 * by environment and is injected at build time — see docs/entra-setup.md.
 */
const apiScope: string = import.meta.env.VITE_ENTRA_API_SCOPE ?? '';
/** Phase 6.5 cutover switch (GitHub variable VITE_DATA_VIA_API): all data through /api for everyone. */
const switchOn = String(import.meta.env.VITE_DATA_VIA_API ?? '').trim().toLowerCase() === 'true';
const serverOnly = apiScope !== '' && switchOn;

export const appConfig = {
  tenantId: '58d0eb75-4e8e-4440-bfd2-97ee0a91c5a8',
  clientId: import.meta.env.VITE_ENTRA_CLIENT_ID ?? '',
  authority:
    'https://login.microsoftonline.com/58d0eb75-4e8e-4440-bfd2-97ee0a91c5a8',
  /** SharePoint hostname + server-relative site path, used to resolve the Graph site ID. */
  sharePoint: {
    hostname: 'netorg2753405.sharepoint.com',
    sitePath: '/sites/SSP-AuditPlus',
  },
  /**
   * Delegated Graph scopes asked for at sign-in. Sites.Selected is granted to
   * the site via PnP (docs/entra-setup.md) and only needed while the browser
   * talks to SharePoint itself; after cutover (serverOnly) the server does,
   * so the SPA no longer asks for it — the permission and the site grant can
   * then be removed without breaking sign-in (entra-setup §8.6).
   */
  graphScopes: serverOnly ? ['User.Read'] : ['User.Read', 'Sites.Selected'],
  graphBaseUrl: 'https://graph.microsoft.com/v1.0',
  /** Delegated scope of the app's own server API ("SSP AuditPlus API", entra-setup §8); '' = not set up. */
  apiScope,
  /** VITE_DATA_VIA_API=true with the API set up — see src/api/mode.ts ('build'). */
  serverOnly,
} as const;

export function assertConfigured(): string | null {
  if (!appConfig.clientId) {
    return 'VITE_ENTRA_CLIENT_ID is not set. Copy .env.example to .env.local and fill in the app registration client ID (see docs/entra-setup.md).';
  }
  // The cutover switch without the API would quietly build the old direct
  // app, which can't work once the site is locked down — say so instead.
  if (switchOn && !appConfig.apiScope) {
    return 'VITE_DATA_VIA_API is true but VITE_ENTRA_API_SCOPE is not set — the app cannot reach its server (docs/entra-setup.md §8.5–8.6).';
  }
  return null;
}
